import { addLog } from 'assets/components/feedback/Feedback';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import { getLocales } from 'assets/locales/Locale';
import { defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import { combineStrings } from 'assets/utils/data/String';
import { isEmail } from 'assets/utils/parsersAndValidation/Validators';
import { HttpStatus } from 'config/Api.Config';
import dayjs from 'dayjs';
import { isEmpty, set } from 'lodash';
import PaymentType from 'models/enums/PaymentType';
import accountReceivableDeleteChequeDocumentApi from 'models/finance/accountReceivable/deleteChequeDocument/Api.AccountReceivable.DeleteChequeDocument';
import accountReceivableSaveApi from 'models/finance/accountReceivable/save/Api.AccountReceivable.Save';
import accountReceivableUploadChequeDocumentApi from 'models/finance/accountReceivable/uploadChequeDocument/Api.AccountReceivable.UploadChequeDocument';
import accountReceivableCommentSaveApi from 'models/finance/accountReceivableComment/save/Api.AccountReceivableComment.Save';
import { AccountReceivableForm, useAccountReceivableProvider } from '../Finance.AccountReceivable.Hooks';
import FinanceAccountReceivableEditInfo from './edit/Finance.AccountReceivable.Edit.Info';
import FinanceAccountReceivableEditNotes from './edit/Finance.AccountReceivable.Edit.Notes';
import FinanceAccountReceivableEditStatements from './edit/Finance.AccountReceivable.Edit.Statements';

export default function FinanceAccountReceivableEdit() {
  const { lang } = getLocales();
  const { selected, pageRouter, listApi } = useAccountReceivableProvider();

  return (
    <ModalForm<AccountReceivableForm>
      subcomponents={ThemeModalFormDefault}
      dependencyArray={[selected]}
      title={lang.accountReceivable}
      default={
        selected
          ? {
              ...selected.toJSON(),
              chequeFile: selected.chequeDocument
                ? {
                    id: selected.chequeDocument.id,
                    dataUrl: selected.chequeDocument.url,
                    name: selected.chequeDocument.name,
                    type: selected.chequeDocument.contentType as Utils.File.MimeType,
                  }
                : null,
              accountReceivableComments: [],
            }
          : {}
      }
      onCancel={pageRouter.updateParams}
      onSubmit={async ({ statementCandidates, chequeFile, accountReceivableComments, ...data }) => {
        const candidates = statementCandidates?.filter((it) => it.isSelected) || [];

        let createdArCount = 0;
        const totalStatements = candidates.length;

        let totalFiles = 0;
        let uploadedFiles = 0;
        let deletedFiles = 0;
        let totalDeletedFiles = 0;

        for (const c of candidates) {
          if (c.statement) {
            const res = await accountReceivableSaveApi({
              ...data,
              createdAtLocal: !data.createdAtLocal ? dayjs().format(defaultServerDateTimeFormat) : data.createdAtLocal,
              statementId: c.statement.id,
              dealerId: data?.id ? c.statement?.dealerId : data.dealerId,
              accountReceivableComments: [],
            });

            if (res?.status !== HttpStatus.ok) {
              addLog({ error: res?.message });
              return;
            }

            createdArCount += 1;

            for (const accountReceivableComment of accountReceivableComments || []) {
              if (accountReceivableComment?.comment) {
                await accountReceivableCommentSaveApi({
                  id: 0,
                  accountReceivableId: res.payload,
                  comment: accountReceivableComment.comment,
                  userId: accountReceivableComment.userId,
                });
              }
            }
            if (chequeFile?.id !== selected?.chequeDocument?.id) {
              const deleteRes = await accountReceivableDeleteChequeDocumentApi(
                res?.payload || data.id,
                selected?.chequeDocument?.id
              );
              if (deleteRes.status === HttpStatus.ok) {
                deletedFiles += 1;
              }
              totalDeletedFiles += 1;
            }
            if (chequeFile && !chequeFile?.id) {
              const uploadRes = await accountReceivableUploadChequeDocumentApi(res?.payload || data.id, {
                formFile: chequeFile.original,
              });
              if (uploadRes.status === HttpStatus.ok) {
                uploadedFiles += 1;
              }
              totalFiles += 1;
            }
          }
        }

        listApi.execute((body) => body);
        pageRouter.updateParams();
        if (uploadedFiles !== totalFiles || deletedFiles !== totalDeletedFiles) {
          addLog({
            warning: combineStrings(
              ' ',
              lang.formSuccessAndFilesFailedMessage,
              `${lang.created} ${createdArCount}/${totalStatements} ARs.`,
              totalStatements > 0 ? `${lang.statements} linked: ${createdArCount}/${totalStatements}.` : null,
              totalFiles > 0 ? `${uploadedFiles}/${totalFiles} ${lang.filesUploaded}` : null,
              totalDeletedFiles > 0 ? `${deletedFiles}/${totalDeletedFiles} ${lang.filesGotDeleted}` : null
            ),
          });
        } else {
          addLog({
            success: combineStrings(
              ' ',
              lang.saveSuccess,
              `${lang.created} ${createdArCount}/${totalStatements} ARs.`,
              totalStatements > 0 ? `${lang.statements} linked: ${createdArCount}/${totalStatements}.` : null
            ),
          });
        }
      }}
      validation={(data, errors) => {
        if (data.statementCandidates && isEmpty(data.statementCandidates?.filter((it) => it.isSelected)))
          set(errors, 'statementCandidates', lang.required);
        if (data.id && (data.statementCandidates?.filter((it) => it.isSelected)?.length ?? 0) > 1)
          set(errors, 'statementCandidates', lang.cannotSelectMoreThanOneCandidate);

        if (!data?.id && !data?.dealerId) set(errors, 'dealerId', lang.required);
        if (!data?.paymentReceivedAtUtc) set(errors, 'paymentReceivedAtUtc', lang.required);
        if (!data?.paymentMethodCode) set(errors, 'paymentMethodCode', lang.required);
        if (!data?.evidenceSource) set(errors, 'evidenceSource', lang.required);

        if (data?.contactEmail && !isEmail(data.contactEmail))
          set(errors, 'contactEmail', lang.invalidEmailFormatRequires);

        if (data?.paymentMethodCode === PaymentType.cheque) {
          if (!data?.chequeNumber) set(errors, 'chequeNumber', lang.required);
          if (!data?.chequeDateAtUtc) set(errors, 'chequeDateAtUtc', lang.required);
          if (!data?.chequeFile) set(errors, 'chequeFile._objectError', lang.required);
        }
      }}
      tabs={[
        { id: 'payment', icon: 'fas-credit-card', text: lang.payment },
        { id: 'statements', icon: 'fas-file', text: lang.statements },
        { id: 'notes', icon: 'fas-file', text: lang.notes },
      ]}
      render={({ activeTab }) => {
        return activeTab.id === 'payment' ? (
          <FinanceAccountReceivableEditInfo />
        ) : activeTab?.id === 'statements' ? (
          <FinanceAccountReceivableEditStatements />
        ) : activeTab?.id === 'notes' ? (
          <FinanceAccountReceivableEditNotes />
        ) : null;
      }}
    />
  );
}
